import React, { useState, useEffect, useContext } from 'react';
import Amplify, { Hub, Auth } from 'aws-amplify';
import config from '../../aws-exports';
import { UserContext } from '../../components/UserContext';
import Lottie from 'react-lottie';
import { default as animationData } from '../../assets/loader.json';
import { cx, css } from '@emotion/css';
import * as assignmentStyles from './assignments.style';
import spellBowlImg from '../../assets/gen_images/batchesicon.png';
import mathsImg from '../../assets/gen_images/mathematics.png';
import generalMathImg from '../../assets/gen_images/general_math.png';
import { useHistory } from 'react-router-dom';
import PracticeNewCard from '../Card/PracticeNewCard';
import { Container } from 'reactstrap';

Amplify.configure(config);

const pageBgStyles = css`
  background-color: #fdfbf7;
  background-image: 
    radial-gradient(at 100% 0%, rgba(241, 102, 2, 0.04) 0px, transparent 50%),
    radial-gradient(at 0% 100%, rgba(244, 203, 14, 0.02) 0px, transparent 50%);
  min-height: 100vh;
  padding-bottom: 100px;
  overflow-x: hidden;
`;

const headerStyles = {
  container: css`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 2rem;
    margin-bottom: 1.5rem;
    margin-top: 2rem;
    border-bottom: 1px solid rgba(241, 102, 2, 0.08);
    padding-bottom: 1.5rem;

    @media (max-width: 991px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 1.5rem;
      margin-bottom: 1.5rem;
      padding-bottom: 1.5rem;
    }
  `,
  leftCol: css`
    flex: 1;
    max-width: 850px;
  `,
  topLabel: css`
    font-family: 'Inter', sans-serif;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.18em;
    color: rgba(241, 102, 2, 0.7);
    margin-bottom: 0.6rem;
    display: block;
  `,
  heading: css`
    font-family: 'Outfit', 'Inter', sans-serif;
    font-size: 2.6rem;
    font-weight: 800;
    font-style: italic;
    line-height: 1.05;
    margin: 0 0 0.6rem 0;
    letter-spacing: -0.02em;
    text-transform: uppercase;

    .orange-part {
      color: #f16602;
      margin-right: 0.6rem;
      display: inline-block;
    }

    .outline-part {
      color: transparent;
      -webkit-text-stroke: 1.5px rgba(241, 102, 2, 0.25);
      font-weight: 900;
      display: inline-block;
    }

    @media (max-width: 768px) {
      font-size: 2rem;
      
      .outline-part {
        -webkit-text-stroke: 1px rgba(241, 102, 2, 0.25);
      }
    }
  `,
  description: css`
    font-family: 'Inter', sans-serif;
    font-size: 1.05rem;
    color: #554a42;
    line-height: 1.6;
    margin: 0;
    max-width: 700px;
    font-weight: 400;

    @media (max-width: 768px) {
      font-size: 0.95rem;
    }
  `
};

function AssignmentCategoryScreen(props) {
  const history = useHistory();
  const [events_category, set_events_category] = useState([]);
  const { user } = useContext(UserContext);
  const [loadedcategories, set_loadedcategories] = useState(false);

  const getToken = async () => {
    try {
      const userObj = await Auth.currentAuthenticatedUser();
      const res = await Auth.currentSession();
      const jwt = res.getAccessToken().getJwtToken();
      const username = userObj.username.includes("Google_") ? userObj.attributes['email'] : userObj.username;
      
      getEventsfromDB(username, jwt, user.batchlist);
    } catch (error) {
      console.error("Auth error in My Challenges:", error);
    }
  };

  useEffect(() => {
    document.title = 'My Challenges | QuizWizard';
    if (user && Object.keys(user).length > 0) {
      getToken();
    }
  }, [user]);

  // Handle Auth changes
  useEffect(() => {
    const authListener = Hub.listen('auth', ({ payload: { event } }) => {
      if (event === 'signOut') {
        history.push('/');
      }
    });

    return () => {
      Hub.remove('auth', authListener);
    };
  }, [history]);

  const getEventsfromDB = async (username, jwt, batchlist) => {
    try {
      // If the user is not enrolled in any batches, they shouldn't see any active challenges.
      // This prevents the backend from falling back to returning challenges the user created (teacher view).
      if (!batchlist || batchlist.length === 0) {
        set_events_category([]);
        set_loadedcategories(true);
        return;
      }

      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: jwt },
        body: JSON.stringify({
          username: username,
          eventtype: 'Assignments',
          batchlist: batchlist,
        }),
      };

      const response = await fetch(
        `https://api.quizwizard.net/quiz/assignmentcounts`,
        requestOptions
      );
      const data = await response.json();
      set_events_category(data);
      set_loadedcategories(true);
    } catch (error) {
      console.error("Error fetching assignment counts:", error);
      set_loadedcategories(true);
    }
  };

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  return (
    <div className={cx(pageBgStyles)}>
      <Container style={{ marginTop: '1rem' }}>
        {/* Curated Header Layout for My Challenges */}
        <div className={cx(headerStyles.container)}>
          <div className={cx(headerStyles.leftCol)}>
            <span className={cx(headerStyles.topLabel)}>PERSONALIZED ASSIGNMENTS</span>
            <h1 className={cx(headerStyles.heading)}>
              <span className="orange-part">Your Active</span>
              <span className="outline-part">Challenges</span>
            </h1>
            <p className={cx(headerStyles.description)}>
              Launch assigned classroom missions, track target criteria, and conquer your school learning modules.
            </p>
          </div>
        </div>

        {loadedcategories ? (
          events_category.length > 0 ? (
            <div className={cx(assignmentStyles.challengeGrid)} style={{ padding: '0 0 80px 0' }}>
              {events_category.map((eventcategory) => {
                const categoryLower = eventcategory.category.toLowerCase();
                let customImg = null;
                
                if (categoryLower.includes('spell')) {
                  customImg = spellBowlImg;
                } else if (categoryLower.includes('mathbowl')) {
                  customImg = mathsImg;
                } else if (categoryLower.includes('math') || categoryLower.includes('algebra')) {
                  customImg = generalMathImg;
                }

                return (
                  <PracticeNewCard
                    key={eventcategory.id}
                    title={eventcategory.category}
                    eventcategory={eventcategory}
                    premiumlevel={user.premiumuser}
                    target="assignmentevents"
                    customImg={customImg}
                  />
                );
              })}
            </div>
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '6rem 2rem', 
              background: '#fffdfb', 
              borderRadius: '2.5rem',
              border: '2.5px dashed rgba(241, 102, 2, 0.15)',
              boxShadow: '0 15px 35px rgba(241, 102, 2, 0.03)'
            }}>
              <div style={{ fontSize: '3.5rem', marginBottom: '1.2rem' }}>🎯</div>
              <h3 style={{ fontWeight: '800', color: '#382110', marginBottom: '0.8rem' }}>No Active Challenges Found</h3>
              <p style={{ color: '#594436', maxWidth: '450px', margin: '0 auto', fontSize: '0.95rem', fontWeight: '500', lineHeight: '1.6' }}>
                You don't have any classroom assignments or active challenges designated right now. Check back soon or explore public practice sessions!
              </p>
            </div>
          )
        ) : (
          <div className="text-center py-5">
            <Lottie options={defaultOptions} height={200} width={200} />
            <p className="mt-3" style={{ fontWeight: '600', color: '#f16602' }}>Loading challenges...</p>
          </div>
        )}
      </Container>
    </div>
  );
}

export default AssignmentCategoryScreen;